import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Mail, Send, CheckCircle, Gift, Clock, Trophy } from 'lucide-react';
import { contactAPI, winnersPublicAPI } from '../services/api';
import { useSubmissionsOpen } from '../hooks/useSubmissionsOpen';
import { toast } from 'sonner';

const NewsletterPopup = () => {
  const location = useLocation();
  const { open: submissionsOpen } = useSubmissionsOpen();
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [popupMode, setPopupMode] = useState('newsletter'); // 'newsletter' or 'earlybird'

  useEffect(() => {
    // Early Bird Extended popup window (Feb 2026 — past, kept harmless).
    const earlyBirdPopupStart = new Date(Date.UTC(2026, 1, 18, 16, 15, 0));
    const earlyBirdPopupEnd = new Date(Date.UTC(2026, 1, 19, 16, 15, 0));
    const now = new Date();
    const isEarlyBirdWindow = now >= earlyBirdPopupStart && now < earlyBirdPopupEnd;

    let timer;
    if (isEarlyBirdWindow) {
      if (localStorage.getItem('wuma_earlybird_popup_feb2026')) return;
      setPopupMode('earlybird');
      timer = setTimeout(() => setIsVisible(true), 5000);
      return () => clearTimeout(timer);
    }

    // Gate on the PUBLIC reveal flag. While the fetch is in flight, show NOTHING
    // (no flash). revealed=false → reveal announcement (own key); revealed=true or
    // fetch failure → standard newsletter (own key).
    let cancelled = false;
    const decide = (mode) => {
      if (cancelled) return;
      const key = mode === 'announcement' ? 'wuma_reveal_announce_2026' : 'wuma_newsletter_popup';
      if (localStorage.getItem(key)) return;   // this popup already seen/closed
      setPopupMode(mode);
      timer = setTimeout(() => { if (!cancelled) setIsVisible(true); }, 15000);
    };
    winnersPublicAPI.hub()
      .then((r) => decide(r.data?.revealed ? 'newsletter' : 'announcement'))
      .catch(() => decide('newsletter'));   // fail-safe: never break, fall back to newsletter

    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, []);

  const handleClose = () => {
    setIsVisible(false);

    if (popupMode === 'earlybird') {
      localStorage.setItem('wuma_earlybird_popup_feb2026', 'closed');
    } else if (popupMode === 'announcement') {
      localStorage.setItem('wuma_reveal_announce_2026', 'closed');
    } else {
      localStorage.setItem('wuma_newsletter_popup', 'closed');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name) {
      toast.error('Please enter your name');
      return;
    }

    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    setIsSubmitting(true);

    try {
      await contactAPI.subscribeNewsletter({ email, name });
      setIsSubscribed(true);
      toast.success('Successfully subscribed to newsletter!');
      
      // Remember subscription
      localStorage.setItem('wuma_newsletter_popup', 'subscribed');
      
      // Auto-close after 3 seconds
      setTimeout(() => {
        setIsVisible(false);
      }, 3000);
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      toast.error(error.response?.data?.detail || 'Failed to subscribe. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isVisible) return null;
  // Hide on auth/admin/judge/finalist routes (critical forms must not be overlaid)
  const HIDDEN_PREFIXES = ['/login', '/system-login', '/super-admin', '/admin', '/judge', '/finalist-upload', '/profile-setup'];
  if (HIDDEN_PREFIXES.some((p) => location.pathname.startsWith(p)) && location.pathname !== '/judges') return null;

  // Early Bird Extended Popup
  if (popupMode === 'earlybird') {
    return (
      <>
        {/* Backdrop - IDENTIQUE à la newsletter */}
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] animate-fade-in"
          onClick={handleClose}
        />

        {/* Popup - IDENTIQUE à la newsletter en structure et couleurs */}
        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[9999] w-full max-w-md mx-4 animate-scale-in">
          <div className="bg-gradient-to-br from-blue-950 to-blue-900 rounded-2xl shadow-2xl border-2 border-orange-500/30 overflow-hidden relative">
            {/* Close Button */}
            <button
              onClick={handleClose}
              className="absolute top-3 right-3 z-10 bg-red-600 hover:bg-red-700 text-white rounded-full p-3 transition-all hover:scale-110 shadow-2xl ring-2 ring-white/50"
              aria-label="Close popup"
            >
              <X className="w-7 h-7" strokeWidth={3} />
            </button>

            <div className="p-8">
              {/* Icon */}
              <div className="flex justify-center mb-4">
                <div className="bg-orange-500/20 rounded-full p-4">
                  <Gift className="w-12 h-12 text-orange-400" />
                </div>
              </div>

              {/* Title */}
              <h2 className="text-3xl font-bold text-center text-white mb-4">
                🎉 Early Bird Extended! 🎉
              </h2>

              {/* Main Message */}
              <div className="bg-orange-500/10 rounded-xl p-4 mb-4 border border-orange-500/30">
                <p className="text-center text-orange-200 text-lg font-semibold">
                  Due to popular demand, Early Bird has been extended until February 28!
                </p>
              </div>

              {/* Secondary Message */}
              <p className="text-center text-blue-200 mb-6 leading-relaxed">
                You asked, we listened! You now have <span className="font-bold text-orange-400">10 more days</span> to take advantage of our Early Bird pricing.
              </p>

              {/* Pricing Reminder */}
              <div className="grid grid-cols-3 gap-2 mb-6">
                <div className="bg-blue-900/50 rounded-lg p-3 text-center border border-orange-500/20">
                  <div className="text-2xl font-bold text-orange-400">$15</div>
                  <div className="text-xs text-blue-300">3 Photos</div>
                </div>
                <div className="bg-blue-900/50 rounded-lg p-3 text-center border border-orange-500/20">
                  <div className="text-2xl font-bold text-orange-400">$35</div>
                  <div className="text-xs text-blue-300">10 Photos</div>
                </div>
                <div className="bg-blue-900/50 rounded-lg p-3 text-center border border-orange-500/20">
                  <div className="text-2xl font-bold text-orange-400">$55</div>
                  <div className="text-xs text-blue-300">20 Photos</div>
                </div>
              </div>

              {/* CTA Button — hidden once submissions are closed */}
              {submissionsOpen && (
                <a
                  href="/submit"
                  className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold py-3 px-6 rounded-lg transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                >
                  <Clock className="w-5 h-5" />
                  Submit Now & Save!
                </a>
              )}

              {/* Close link */}
              <div className="mt-4 flex justify-center">
                <button
                  onClick={handleClose}
                  className="text-red-400 hover:text-red-300 font-semibold text-sm underline transition-colors"
                >
                  Close this popup
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Custom CSS - IDENTIQUE à la newsletter */}
        <style>{`
          @keyframes fade-in {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes scale-in {
            from { opacity: 0; transform: translate(-50%, -50%) scale(0.9); }
            to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
          }
          .animate-fade-in { animation: fade-in 0.3s ease-out; }
          .animate-scale-in { animation: scale-in 0.4s ease-out; }
        `}</style>
      </>
    );
  }

  // Reveal Announcement Popup (teaser before July 14th) — no form
  if (popupMode === 'announcement') {
    return (
      <>
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] animate-fade-in"
          onClick={handleClose}
          data-testid="reveal-announce-backdrop"
        />
        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[9999] w-full max-w-md mx-4 animate-scale-in" data-testid="reveal-announce-popup">
          <div className="bg-gradient-to-br from-blue-950 to-blue-900 rounded-2xl shadow-2xl border-2 border-orange-500/30 overflow-hidden relative">
            <button
              onClick={handleClose}
              className="absolute top-3 right-3 z-10 bg-red-600 hover:bg-red-700 text-white rounded-full p-3 transition-all hover:scale-110 shadow-2xl ring-2 ring-white/50"
              aria-label="Close popup"
              title="Close"
              data-testid="reveal-announce-close-x"
            >
              <X className="w-7 h-7" strokeWidth={3} />
            </button>

            <div className="p-8">
              <div className="flex justify-center mb-4">
                <div className="bg-orange-500/20 rounded-full p-4">
                  <Trophy className="w-12 h-12 text-orange-400" />
                </div>
              </div>

              <h2 className="text-2xl sm:text-3xl font-bold text-center text-white mb-4 leading-tight">
                WINNERS &amp; PRIZES OF WUMA 2026
              </h2>

              <div className="bg-orange-500/10 rounded-xl p-4 mb-4 border border-orange-500/30">
                <p className="text-center text-orange-200 text-lg font-semibold flex items-center justify-center gap-2">
                  <Clock className="w-5 h-5 flex-shrink-0" />
                  Revealed on Bastille Day, July 14th 2026
                </p>
              </div>

              <p className="text-center text-blue-200 mb-6 leading-relaxed">
                The best macro shots of 2026 are about to surface.
              </p>

              <div className="flex justify-center">
                <button
                  onClick={handleClose}
                  className="text-red-400 hover:text-red-300 font-semibold text-sm underline transition-colors"
                  data-testid="reveal-announce-close-link"
                >
                  Close this popup
                </button>
              </div>
            </div>
          </div>
        </div>

        <style>{`
          @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
          @keyframes scale-in {
            from { opacity: 0; transform: translate(-50%, -50%) scale(0.9); }
            to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
          }
          .animate-fade-in { animation: fade-in 0.3s ease-out; }
          .animate-scale-in { animation: scale-in 0.4s ease-out; }
        `}</style>
      </>
    );
  }

  // Newsletter Popup (default)
  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] animate-fade-in"
        onClick={handleClose}
      />

      {/* Popup */}
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[9999] w-full max-w-md mx-4 animate-scale-in">
        <div className="bg-gradient-to-br from-blue-950 to-blue-900 rounded-2xl shadow-2xl border-2 border-orange-500/30 overflow-hidden relative">
          {/* Close Button Top Right - TRÈS VISIBLE */}
          <button
            onClick={handleClose}
            className="absolute top-3 right-3 z-10 bg-red-600 hover:bg-red-700 text-white rounded-full p-3 transition-all hover:scale-110 shadow-2xl ring-2 ring-white/50"
            aria-label="Close popup"
            title="Fermer"
          >
            <X className="w-7 h-7" strokeWidth={3} />
          </button>

          {!isSubscribed ? (
            <div className="p-8">
              {/* Icon */}
              <div className="flex justify-center mb-4">
                <div className="bg-orange-500/20 rounded-full p-4">
                  <Mail className="w-12 h-12 text-orange-400" />
                </div>
              </div>

              {/* Title */}
              <h2 className="text-3xl font-bold text-center text-white mb-3">
                Stay Updated! 🌊
              </h2>
              
              {/* Description */}
              <p className="text-center text-blue-200 mb-6 leading-relaxed">
                Subscribe to our newsletter and be the first to know about WUMA 2026 competition updates, winner announcements, and underwater photography tips!
              </p>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Name Input (Required) */}
                <div>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    required
                    className="w-full px-4 py-3 bg-blue-900/50 border border-blue-700 rounded-lg text-white placeholder-blue-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                  />
                </div>

                {/* Email Input */}
                <div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    className="w-full px-4 py-3 bg-blue-900/50 border border-blue-700 rounded-lg text-white placeholder-blue-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold py-3 px-6 rounded-lg transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Subscribing...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Subscribe Now
                    </>
                  )}
                </button>
              </form>

              {/* Footer Text */}
              <p className="text-xs text-center text-blue-300 mt-4">
                We respect your privacy. Unsubscribe anytime.
              </p>

              {/* Close Button Bottom - CENTRÉ POUR MOBILE */}
              <div className="mt-4 flex justify-center">
                <button
                  onClick={handleClose}
                  className="text-red-400 hover:text-red-300 font-semibold text-sm underline transition-colors"
                >
                  Close this popup
                </button>
              </div>
            </div>
          ) : (
            <div className="p-8 text-center">
              {/* Success Icon */}
              <div className="flex justify-center mb-4">
                <div className="bg-green-500/20 rounded-full p-4">
                  <CheckCircle className="w-12 h-12 text-green-400" />
                </div>
              </div>

              {/* Success Message */}
              <h2 className="text-2xl font-bold text-white mb-3">
                You're All Set! 🎉
              </h2>
              <p className="text-blue-200 leading-relaxed mb-6">
                Thank you for subscribing! We'll keep you updated on all things WUMA 2026.
              </p>

              {/* Close Button Bottom - CENTRÉ */}
              <button
                onClick={handleClose}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded-lg transition-all shadow-lg"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Custom CSS for animations */}
      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes scale-in {
          from {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.9);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }

        .animate-scale-in {
          animation: scale-in 0.4s ease-out;
        }
      `}</style>
    </>
  );
};

export default NewsletterPopup;
